import React, { memo, useState, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { 
  Globe, 
  Bot, 
  User,
  Clock,
  AlertTriangle,
  CheckCircle,
  Tag,
  Pin,
  Archive,
  ChevronRight,
  CheckSquare,
  RotateCcw,
  X
} from 'lucide-react';

// Import channel icons as SVG components for better visual
const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const MessengerIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 0C5.373 0 0 4.974 0 11.111c0 3.498 1.744 6.614 4.469 8.654V24l4.088-2.242c1.092.3 2.246.464 3.443.464 6.627 0 12-4.974 12-11.111S18.627 0 12 0zm1.191 14.963l-3.055-3.26-5.963 3.26L10.732 8l3.131 3.259L19.752 8l-6.561 6.963z"/>
  </svg>
);

const InstagramIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '@/components/ui/context-menu';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

const getChannelIcon = (channel) => {
  switch(channel) {
    case 'whatsapp': return <WhatsAppIcon className="w-3 h-3 text-white" />;
    case 'instagram': return <InstagramIcon className="w-3 h-3 text-white" />;
    case 'messenger': return <MessengerIcon className="w-3 h-3 text-white" />;
    default: return <Globe className="w-3 h-3 text-white" />;
  }
};

const getStatusColor = (status, priority) => {
  if (priority === 'urgent') return 'border-l-red-500';
  if (priority === 'high') return 'border-l-orange-500';
  switch(status) {
    case 'urgent': return 'border-l-red-500';
    case 'attention': return 'border-l-amber-500';
    case 'resolved': return 'border-l-slate-400';
    default: return 'border-l-green-500';
  }
};

const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  
  const now = new Date();
  let time;
  
  if (typeof timestamp === 'string') {
    // If ISO string without timezone suffix, assume UTC
    if (!timestamp.endsWith('Z') && !timestamp.includes('+')) {
      time = new Date(timestamp + 'Z');
    } else {
      time = new Date(timestamp);
    }
  } else {
    time = new Date(timestamp);
  }
  
  if (isNaN(time.getTime())) return '-';
  
  const diff = Math.floor((now - time) / 1000);

  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 172800) return 'Yesterday';
  
  return time.toLocaleDateString('id-ID', { 
    day: 'numeric', 
    month: 'short',
    timeZone: 'Asia/Jakarta'
  });
};

// ============================================================================
// CONVERSATION ITEM COMPONENT
// ============================================================================

export const ConversationItem = memo(({
  conversation,
  isSelected = false,
  isActive = false,
  isSelectionMode = false,
  onSelect,
  onClick,
  onQuickPin,
  onQuickArchive,
  onSetPriority,
  onQuickStatusChange,
  onClearAttention,
  onEnterSelectionMode
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  
  const {
    id,
    customer_name,
    customer_phone,
    customer_avatar,  // Profile picture URL (Instagram: customer_avatar, Messenger: customer_profile_pic)
    customer_profile_pic, // Messenger profile pic
    customer_username,
    platform_sender_id,
    channel,
    handled_by,
    status,
    priority,
    unread_count = 0,
    last_message_at,
    last_message_preview,
    tags = [],
    is_pinned = false,
    is_archived = false,
    needs_attention = false,
    sla_status
  } = conversation;

  const isUnread = unread_count > 0;
  const isOverdue = sla_status === 'overdue';
  const isAttention = needs_attention === true;

  // For Instagram: determine display name and subtitle
  const isIgNumericId = channel === 'instagram' && customer_name && /^\d+$/.test(customer_name);
  const displayName = isIgNumericId 
    ? (customer_username ? `@${customer_username}` : 'Instagram User') 
    : (customer_name || 'Unknown');
  const igSubtitle = channel === 'instagram' 
    ? (isIgNumericId 
        ? `ID: ${customer_name}` 
        : (customer_username && customer_username !== customer_name?.replace('@','') ? `@${customer_username}` : null))
    : null;

  // Long press handlers for mobile
  const handleTouchStart = useCallback((e) => {
    const timer = setTimeout(() => {
      // Trigger context menu on long press
      setShowContextMenu(true);
    }, 500);
    setLongPressTimer(timer);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  }, [longPressTimer]);

  const handleContextAction = useCallback((action, value) => {
    setShowContextMenu(false);
    switch(action) {
      case 'select':
        // Enter selection mode and select this item
        onEnterSelectionMode?.();
        onSelect?.(id);
        break;
      case 'pin':
        onQuickPin?.(id);
        break;
      case 'archive':
        onQuickArchive?.(id);
        break;
      case 'priority':
        onSetPriority?.(id, value);
        break;
      case 'status_change':
        onQuickStatusChange?.(id, value);
        break;
      case 'clear_attention':
        onClearAttention?.(id);
        break;
    }
  }, [id, onSelect, onQuickPin, onQuickArchive, onSetPriority, onQuickStatusChange, onEnterSelectionMode]);

  const cardContent = (
    <div
      data-testid={`conversation-item-${id}`}
      className={cn(
        'group relative flex items-start gap-3 p-3 sm:p-4',
        'bg-white dark:bg-zinc-900 rounded-xl',
        'border-l-4 border border-slate-200 dark:border-zinc-800',
        'hover:shadow-md hover:border-slate-300 dark:hover:border-zinc-700',
        'transition-all duration-200 cursor-pointer',
        'select-none', // Prevent text selection on long press
        getStatusColor(status, priority),
        isSelected && 'ring-2 ring-blue-500 border-indigo-300',
        isActive && 'ring-2 ring-blue-500',
        isOverdue && 'border-l-red-500'
      )}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {/* Checkbox - Only visible in selection mode */}
      {isSelectionMode && (
        <div 
          className="flex-shrink-0 flex items-center pt-1"
          onClick={(e) => {
            e.stopPropagation();
            onSelect?.(id);
          }}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => {}}
            className={cn(
              'data-[state=checked]:bg-indigo-600 transition-all duration-200',
              'w-5 h-5 border-2'
            )}
          />
        </div>
      )}

      {/* Avatar */}
      <div className="flex-shrink-0 relative">
        {(customer_avatar || customer_profile_pic) ? (
          <img 
            src={customer_avatar || customer_profile_pic} 
            alt={customer_name || 'Customer'}
            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
            onError={(e) => {
              // Fallback to initials if image fails to load
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
        ) : null}
        <div 
          className={cn(
            'w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center',
            'bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-600',
            'text-base sm:text-lg font-semibold text-slate-600 dark:text-zinc-300',
            (customer_avatar || customer_profile_pic) ? 'hidden' : ''
          )}
        >
          {/* For Instagram, use first letter after @ */}
          {channel === 'instagram' && customer_name?.startsWith('@')
            ? customer_name.charAt(1)?.toUpperCase() || '?'
            : customer_name?.charAt(0)?.toUpperCase() || '?'
          }
        </div>
        
        {/* Channel Indicator */}
        <div className={cn(
          'absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full',
          'border-2 border-white dark:border-slate-800',
          'flex items-center justify-center',
          channel === 'whatsapp' ? 'bg-green-500' :
          channel === 'messenger' ? 'bg-indigo-500' :
          channel === 'instagram' ? 'bg-pink-500' : 'bg-amber-500'
        )}>
          {getChannelIcon(channel)}
        </div>

        {/* Pinned Indicator */}
        {is_pinned && (
          <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-indigo-500 flex items-center justify-center">
            <Pin className="w-3 h-3 text-white fill-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-1">
        {/* Name + Handler + Time Row */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0">
              <h3 className={cn(
                'truncate text-sm',
                isUnread 
                  ? 'font-bold text-slate-900 dark:text-zinc-50' 
                  : 'font-medium text-slate-700 dark:text-zinc-200'
              )}>
                {displayName}
              </h3>
            </div>
            
            {/* Handler Badge - Next to name */}
            <Badge 
              variant="outline" 
              className={cn(
                'h-5 px-1.5 text-[10px] gap-1 flex-shrink-0',
                handled_by === 'bot' 
                  ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300' 
                  : 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800 text-indigo-700 dark:text-indigo-300'
              )}
            >
              {handled_by === 'bot' ? <Bot className="w-3 h-3" /> : <User className="w-3 h-3" />}
              <span className="hidden sm:inline">{handled_by === 'bot' ? 'Bot' : 'Agent'}</span>
            </Badge>

            {/* Priority Badge — sejajar di kanan badge Bot/Agent (bukan di baris bawah) */}
            {priority && priority !== 'normal' && (
              <Badge
                variant="outline"
                className={cn(
                  'h-5 px-1.5 text-[10px] capitalize flex-shrink-0 gap-0.5',
                  priority === 'urgent' && 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
                  priority === 'high' && 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-400',
                  priority === 'low' && 'bg-slate-50 dark:bg-zinc-800 border-slate-200 dark:border-zinc-700 text-slate-500 dark:text-zinc-400'
                )}
                data-testid={`priority-badge-${id}`}
              >
                {priority === 'urgent' && <AlertTriangle className="w-3 h-3" />}
                {priority}
              </Badge>
            )}
            
            {/* Attention Badge — bisa diklik untuk menandai sudah ditangani */}
            {isAttention && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onClearAttention?.(id); }}
                title="Sudah ditangani — klik untuk hapus label Attention"
                className="h-5 px-1.5 rounded-md text-[10px] font-medium inline-flex items-center gap-0.5 flex-shrink-0 border bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800 transition-colors hover:bg-amber-200 dark:hover:bg-amber-900/50"
                data-testid={`attention-badge-${id}`}
              >
                <AlertTriangle className="w-3 h-3" />
                <span className="hidden sm:inline">Attention</span>
                <X className="w-2.5 h-2.5 opacity-70" />
              </button>
            )}
            
            {/* Unread Badge */}
            {isUnread && (
              <Badge 
                variant="destructive" 
                className="h-5 min-w-[20px] px-1.5 text-[10px] font-bold flex-shrink-0"
              >
                {unread_count > 99 ? '99+' : unread_count}
              </Badge>
            )}
          </div>
          
          <span className={cn(
            'text-xs flex-shrink-0',
            isUnread ? 'text-indigo-600 dark:text-indigo-400 font-medium' : 'text-slate-500 dark:text-zinc-400'
          )}>
            {formatTime(last_message_at)}
          </span>
        </div>

        {/* Badges Row - SLA, Arsip, Tags (priority sudah pindah ke baris nama) */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* SLA Overdue */}
          {isOverdue && (
            <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">
              <Clock className="w-3 h-3 mr-0.5" />
              Overdue
            </Badge>
          )}

          {/* Archived Badge */}
          {is_archived && (
            <Badge variant="outline" className="h-5 px-1.5 text-[10px] bg-slate-100 dark:bg-zinc-800">
              <Archive className="w-3 h-3 mr-0.5" />
              Arsip
            </Badge>
          )}

          {/* Tags */}
          {tags.slice(0, 2).map((tag, idx) => (
            <Badge 
              key={idx} 
              variant="outline" 
              className="h-5 px-1.5 text-[10px] bg-slate-50 dark:bg-zinc-800 hidden sm:flex"
            >
              {tag}
            </Badge>
          ))}
          {tags.length > 2 && (
            <Badge variant="outline" className="h-5 px-1.5 text-[10px] hidden sm:flex">
              +{tags.length - 2}
            </Badge>
          )}
        </div>

        {/* Message Preview */}
        <p className={cn(
          'text-xs sm:text-sm truncate',
          isUnread 
            ? 'text-slate-700 dark:text-zinc-300' 
            : 'text-slate-500 dark:text-zinc-400'
        )}>
          {last_message_preview || 'Belum ada pesan'}
        </p>
      </div>

      {/* Right Side: Status Icon + Arrow */}
      <div className="flex-shrink-0 flex flex-col items-end justify-center gap-2">
        {/* Status Icon */}
        <div className="hidden sm:flex items-center">
          {status === 'urgent' && <AlertTriangle className="w-4 h-4 text-red-500" />}
          {status === 'resolved' && <CheckCircle className="w-4 h-4 text-green-500" />}
        </div>
        
        {/* Hover Arrow
        <ChevronRight className={cn(
          'w-5 h-5 text-slate-300 transition-opacity',
          isHovered ? 'opacity-100' : 'opacity-0'
        )} /> */}
      </div>
    </div>
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        {cardContent}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        {/* Select */}
        <ContextMenuItem 
          onClick={() => handleContextAction('select')}
          className="cursor-pointer"
        >
          <CheckSquare className="w-4 h-4 mr-2" />
          Select
        </ContextMenuItem>
        
        {/* <ContextMenuSeparator /> */}
        
        {/* Pin/Unpin */}
        <ContextMenuItem 
          onClick={() => handleContextAction('pin')}
          className="cursor-pointer"
        >
          <Pin className={cn('w-4 h-4 mr-2', is_pinned && 'text-indigo-600 fill-blue-600')} />
          {is_pinned ? 'Unpin' : 'Pin ke Atas'}
        </ContextMenuItem>
        
        {/* Archive/Unarchive */}
        <ContextMenuItem 
          onClick={() => handleContextAction('archive')}
          className="cursor-pointer"
        >
          <Archive className="w-4 h-4 mr-2" />
          {is_archived ? 'Kembalikan dari Arsip' : 'Arsipkan'}
        </ContextMenuItem>
        
        {/* Tandai Selesai / Buka Kembali */}
        {(status === 'open' || status === 'active') && (
          <ContextMenuItem 
            onClick={() => handleContextAction('status_change', 'resolved')}
            className="cursor-pointer text-green-600 dark:text-green-400"
            data-testid={`context-resolve-${id}`}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Tandai Selesai
          </ContextMenuItem>
        )}
        {(status === 'resolved' || status === 'closed') && (
          <ContextMenuItem 
            onClick={() => handleContextAction('status_change', 'open')}
            className="cursor-pointer text-indigo-600 dark:text-indigo-400"
            data-testid={`context-reopen-${id}`}
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Buka Kembali
          </ContextMenuItem>
        )}
        
        {/* <ContextMenuSeparator /> */}
        
        {/* Hapus label Attention */}
        {isAttention && (
          <ContextMenuItem 
            onClick={() => handleContextAction('clear_attention')}
            className="cursor-pointer text-amber-600 dark:text-amber-400"
            data-testid={`context-clear-attention-${id}`}
          >
            <AlertTriangle className="w-4 h-4 mr-2" />
            Attention: sudah ditangani
          </ContextMenuItem>
        )}
        
        {/* Priority Submenu */}
        <ContextMenuSub>
          <ContextMenuSubTrigger className="cursor-pointer">
            <Tag className="w-4 h-4 mr-2" />
            Set Priority
          </ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-40">
            <ContextMenuItem 
              onClick={() => handleContextAction('priority', 'urgent')}
              className="cursor-pointer text-red-600 dark:text-red-400"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              Urgent
              {priority === 'urgent' && <CheckCircle className="w-3 h-3 ml-auto" />}
            </ContextMenuItem>
            <ContextMenuItem 
              onClick={() => handleContextAction('priority', 'high')}
              className="cursor-pointer text-orange-600 dark:text-orange-400"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              High
              {priority === 'high' && <CheckCircle className="w-3 h-3 ml-auto" />}
            </ContextMenuItem>
            <ContextMenuItem 
              onClick={() => handleContextAction('priority', 'normal')}
              className="cursor-pointer"
            >
              <Tag className="w-4 h-4 mr-2" />
              Normal
              {(!priority || priority === 'normal') && <CheckCircle className="w-3 h-3 ml-auto" />}
            </ContextMenuItem>
            <ContextMenuItem 
              onClick={() => handleContextAction('priority', 'low')}
              className="cursor-pointer text-slate-500 dark:text-zinc-400"
            >
              <Tag className="w-4 h-4 mr-2" />
              Low
              {priority === 'low' && <CheckCircle className="w-3 h-3 ml-auto" />}
            </ContextMenuItem>
          </ContextMenuSubContent>
        </ContextMenuSub>
      </ContextMenuContent>
    </ContextMenu>
  );
});

ConversationItem.displayName = 'ConversationItem';

export default ConversationItem;
